import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useFetch, http } from "@/lib/api";
import { PageHeader, Loading, StatCard, SectionTitle, Copyable } from "@/components/common";
import AIResearch from "@/components/AIResearch";
import { fmtUsd, timeAgo } from "@/lib/format";
import { toast } from "sonner";
import { Hammer, ArrowLeft, GitBranch } from "lucide-react";

export default function DevProfile() {
  const { address } = useParams();
  const nav = useNavigate();
  const { data, loading } = useFetch(`/developers/${address}`, [address]);
  if (loading || !data) return <Loading />;
  const d = data.data;

  const follow = async () => {
    await http.post("/tracked-developers", { kind: "developer", ref_id: d.address, label: d.short, sub: `${d.launches_count} launches` });
    toast.success("Added to Proven Dev Watchlist");
  };

  return (
    <div className="fade-in">
      <button onClick={() => nav(-1)} className="mb-3 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-amber-400">
        <ArrowLeft className="h-3.5 w-3.5" /> Back
      </button>
      <PageHeader
        icon={Hammer}
        title={`Developer ${d.short}`}
        subtitle={<Copyable value={d.address} display={d.address} />}
        actions={<button data-testid="dev-follow-btn" onClick={follow} className="rounded-lg border border-amber-600/40 bg-amber-500/10 px-3 py-1.5 text-xs font-semibold text-amber-300 hover:bg-amber-500/20">Follow Developer</button>}
      />

      <div className="mb-5 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
        <StatCard label="Analyzable Launches" value={d.launches_count} />
        <StatCard label="Reached $500K+" value={d.reached_500k} tone="pos" />
        <StatCard label="Reached $1M+" value={d.reached_1m} tone="pos" />
        <StatCard label="Reached $5M+" value={d.reached_5m} tone="pos" />
        <StatCard label="Avg Initial Liq" value={fmtUsd(d.avg_initial_liq)} />
        <StatCard label="Avg Time to Peak" value={`${d.avg_time_to_peak_min}m`} />
      </div>

      <div className="grid gap-5 lg:grid-cols-3">
        <div className="pnut-card p-5">
          <SectionTitle right={<GitBranch className="h-4 w-4 text-slate-500" />}>Provenance</SectionTitle>
          <div className="space-y-2 text-xs">
            <div className="flex justify-between"><span className="text-slate-500">Label</span><span className="text-slate-200">{d.label}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Funding source</span><span className="font-mono text-slate-200">{d.funding_source}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Data quality</span><span className="text-slate-200">{d.data_quality}</span></div>
          </div>
          <div className="mt-3">
            <div className="mb-1.5 font-mono text-[10px] uppercase text-slate-500">Related wallets</div>
            <div className="flex flex-wrap gap-1.5">
              {d.related_wallets.map((r, i) => (<span key={i} className="rounded border border-slate-800 bg-void/50 px-2 py-0.5 font-mono text-[10px] text-slate-400">{r}</span>))}
            </div>
          </div>
          <p className="mt-3 text-[11px] leading-relaxed text-slate-500">
            Relationships are labeled by confidence. With {d.data_quality.toLowerCase()} data quality, treat interpretations with appropriate caution.
          </p>
        </div>

        <div className="pnut-card p-5 lg:col-span-2">
          <SectionTitle>Complete Launch History</SectionTitle>
          <div className="max-h-80 overflow-auto">
            <table className="pnut-table w-full">
              <thead><tr><th>Token</th><th>Platform</th><th>Init Liq</th><th>Init MC</th><th>Peak MC</th><th>Time to Peak</th><th>Dev Alloc</th><th>Survival</th></tr></thead>
              <tbody>
                {d.launches.map((l, i) => (
                  <tr key={i} onClick={() => l.token_id && nav(`/token/${l.token_id}`)}>
                    <td className="font-semibold text-slate-200">${l.symbol}</td>
                    <td className="text-slate-400">{l.platform}</td>
                    <td className="font-mono text-slate-400">{fmtUsd(l.initial_liq)}</td>
                    <td className="font-mono text-slate-400">{fmtUsd(l.initial_mc)}</td>
                    <td className="font-mono text-slate-200">{fmtUsd(l.peak_mc)}</td>
                    <td className="font-mono text-slate-400">{l.time_to_peak_min}m</td>
                    <td className="font-mono text-slate-400">{l.dev_alloc_pct}%</td>
                    <td className={l.survival === "Rugged" ? "text-rose-400" : l.survival === "Alive" ? "text-emerald-400" : "text-slate-400"}>{l.survival}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="mt-5">
        <AIResearch kind="developer" refId={d.address} presets={["How many tokens has this developer launched?", "What % reached major milestones?", "How does the developer manage liquidity?"]} />
      </div>
    </div>
  );
}
